import axios from "axios";
import { API } from "../../../backend";
import { useAppContext } from "../../../hooks/context/AppContext";
import { useState, useMemo } from "react";
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

export const useKPISummaryApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh(
    (token: any | null, account: any | null) => {
      setIdToken(token);
    }
  );

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken
          ? `Bearer ${idToken}`
          : `Bearer ${authData.idToken}`,
      },
    });

    // Add response interceptor to handle 401 errors
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const { token } = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error("Error refreshing token:", refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
    return instance;
  }, [idToken, authData.idToken]);

   
  const getKPISInScopeOptions = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get("/lookup/KPIS_In_Scope");
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // @ts-ignore
      return handleError(error, "fetching KPIS In Scope Options");
    }
  };
  const getESLowCostSuppliersinScope = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_LowCost_Suppliersin_Scope');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching low cost suppliers options');
    }
  };
  const getCptCompliance = async () => {
    try {
      const response = await apiClient.get('/statusFlag');
      console.log("CPT compliance response:", response.data);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching CPT compliance options');
    }
  };
  const getDesignToDistributeComplete = async () => {
    try {
      const response = await apiClient.get('/lookup/LS_Design_to_Distribute_Complete');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Design To Distribute Complete options');
    }
  };
  const getSupplierUtilisation = async () => {
    try {
      const response = await apiClient.get('/lookup/PS_Supplier_Utilisation');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching supplier utilisation options');
    }
  };
  const getESManufacturingComplete = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_Manufacturing_Comeple');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching manufacturing complete options');
    }
  };
   
   const getTechnicalScopeOfChange = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get('/lookup/TS_PackType');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
       // @ts-ignore
      return handleError(error, 'fetching Technical Scope Of Change options');
    }
  };
  const getKPISCondition = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get('/lookup/PS_inhouse');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
       // @ts-ignore
      return handleError(error, 'fetching KPIS Condition options');
    }  
  };
 const getLaunchYears = async () => {
    try {
      const response = await apiClient.get('/launchYears');
      console.log("Launch years response:", response.data);
      return Array.isArray(response.data.launchYears) ? response.data.launchYears : [];
    } catch (error) {
      return handleError(error, 'fetching launch years');
    }
  };

  // const getLaunchQuarter = async () => {
  //   try {
  //     const response = await apiClient.get('/launchQuarter');
  //     console.log("Launch quarters response:", response.data.data);
  //     return Array.isArray(response.data.data) ? response.data.data : [];
  //   } catch (error) {
  //     return handleError(error, 'fetching launch quarters');
  //   }
  // };

  const getWeeks = async (year: string, quarter: string) => {
    try {
      let url = '/launchWeek';
      
      if (year && quarter) {
        url = `/launchWeek?year=${year}&quarter=${quarter}`;
      } else if (year) {
        url = `/launchWeek?year=${year}`;
      } else if (quarter) {
        url = `/launchWeek?quarter=${quarter}`;
      }
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching launchWeek');
    }
  };
  
  const createKpiSummary = async (payload: any) => {
    try {
      const response = await apiClient.post("kpisummary/submit", payload);
      return response.data;
    } catch (err) {
      return handleError(err, "creating KPI summary");
    }
  };
  const getKpiSummaryById = async (id: any) => {
    try {
      const response = await apiClient.get(`/kpisummary/${id}`);
      console.log("KPI Summary by ID response:", response.data);  
      return response.data;
    } catch (err) {
      return handleError(err, "fetching KPI summary details");
    }
  }; 
  return {
    getKPISInScopeOptions,
    getTechnicalScopeOfChange,
    getESLowCostSuppliersinScope,
    getCptCompliance,
    getDesignToDistributeComplete,
    getSupplierUtilisation,
    getESManufacturingComplete,
    getLaunchYears,
    getWeeks,
    createKpiSummary,
    getKpiSummaryById,
    getKPISCondition
  };
};
